/**
 * Tipos de resultado baseado na estrutura de resultData
 */
export type ResultDisplayType = 'counter' | 'monetary' | 'list' | 'multiColumn';

/**
 * Interface para header de tabela
 */
export interface TableHeader {
    key: string;
    label: string;
}

/**
 * Classe utilitária para processamento de resultados do AI Search
 */
export class SpotlightResultProcessor {

    /**
     * Mapeamento de headers para português
     */
    private static readonly HEADER_LABELS: { [key: string]: string } = {
        // Campos comuns
        'nome': 'Nome',
        'total': 'Total',
        'valor': 'Valor',
        'data': 'Data',
        'tipo': 'Tipo',
        'descricao': 'Descrição',
        'status': 'Status',
        // Membros
        'telefone': 'Telefone',
        'email': 'E-mail',
        'idade': 'Idade',
        'genero': 'Gênero',
        'homens': 'Homens',
        'mulheres': 'Mulheres',
        // Financeiro
        'banco': 'Banco',
        'saldo': 'Saldo',
        'forma_pagamento': 'Pagamento',
        'conta': 'Conta',
        'agencia': 'Agência',
        // Grupos
        'divisao': 'Divisão',
        'lider': 'Líder',
        'membros': 'Membros',
        // Outros
        'quantidade': 'Quantidade',
        'percentual': 'Percentual',
        'media': 'Média',
        'fornecedor': 'Fornecedor',
        'categoria': 'Categoria',
        'estado_civil': 'Estado Civil',
        'endereco': 'Endereço',
        'cidade': 'Cidade',
        'bairro': 'Bairro',
        'cep': 'CEP',
        'cpf': 'CPF',
        'rg': 'RG',
        'profissao': 'Profissão',
        'nascimento': 'Nascimento',
        'batismo': 'Batismo',
        'foto': 'Foto',
        'frequencia': 'Frequência'
    };

    /**
     * Campos que indicam valor monetário
     */
    private static readonly MONETARY_FIELDS = ['total', 'valor', 'saldo', 'amount', 'soma'];

    /**
     * Campos que indicam contagem numérica
     */
    private static readonly COUNTER_FIELDS = ['total', 'count', 'quantidade', 'homens', 'mulheres'];

    /**
     * Detecta o tipo de exibição baseado no resultData
     */
    static detectDisplayType(resultData: any[] | null): ResultDisplayType {
        if (!resultData || resultData.length === 0) {
            return 'counter';
        }

        const firstItem = resultData[0];
        const keys = Object.keys(firstItem);

        // Se tem múltiplos registros, é lista
        if (resultData.length > 1) {
            return 'list';
        }

        // Se tem apenas 1 registro
        if (resultData.length === 1) {
            // Se tem apenas 1 campo e é numérico
            if (keys.length === 1) {
                const key = keys[0];
                const value = firstItem[key];

                // Verifica se é monetário pelo valor (tem casas decimais ou é string com ponto)
                if (this.isMonetaryValue(value, key)) {
                    return 'monetary';
                }

                return 'counter';
            }

            // Se tem múltiplas colunas mas apenas 1 registro (ex: homens/mulheres)
            if (keys.length > 1) {
                return 'multiColumn';
            }
        }

        return 'counter';
    }

    /**
     * Verifica se um valor é monetário
     */
    private static isMonetaryValue(value: any, key: string): boolean {
        // Se o campo é conhecido como monetário
        if (['valor', 'saldo', 'amount'].includes(key.toLowerCase())) {
            return true;
        }

        // Se é string com casas decimais significativas
        if (typeof value === 'string' && value.includes('.')) {
            const decimals = value.split('.')[1];
            if (decimals && decimals.length === 2 && parseFloat(value) > 100) {
                return true;
            }
        }

        return false;
    }

    /**
     * Extrai os headers da tabela a partir do primeiro item
     */
    static extractHeaders(resultData: any[] | null): TableHeader[] {
        if (!resultData || resultData.length === 0) {
            return [];
        }

        const firstItem = resultData[0];
        const keys = Object.keys(firstItem);

        return keys.map(key => ({
            key,
            label: this.getHeaderLabel(key)
        }));
    }

    /**
     * Obtém o label traduzido para um header
     */
    static getHeaderLabel(key: string): string {
        const lowerKey = key.toLowerCase();

        // Primeiro tenta o mapeamento direto
        if (this.HEADER_LABELS[lowerKey]) {
            return this.HEADER_LABELS[lowerKey];
        }

        // Fallback: converte snake_case para Title Case
        return this.snakeToTitleCase(key);
    }

    /**
     * Converte snake_case para Title Case
     */
    private static snakeToTitleCase(str: string): string {
        return str
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    /**
     * Formata um valor para exibição
     */
    static formatValue(value: any, key: string): string {
        if (value === null || value === undefined) {
            return '-';
        }

        const lowerKey = key.toLowerCase();

        // Formatação de valores monetários
        if (['valor', 'saldo', 'total', 'amount'].includes(lowerKey)) {
            return this.formatCurrency(value);
        }

        // Formatação de datas
        if (['data', 'date', 'created_at', 'updated_at'].includes(lowerKey)) {
            return this.formatDate(value);
        }

        // Formatação de forma de pagamento
        if (lowerKey === 'forma_pagamento') {
            return this.formatPaymentType(value);
        }

        // Formatação de tipo de entrada
        if (lowerKey === 'tipo') {
            return this.formatEntryType(value);
        }

        // Formatação de estado civil
        if (lowerKey === 'estado_civil') {
            return this.formatMaritalStatus(value);
        }

        return String(value);
    }

    /**
     * Formata valor monetário para BRL
     */
    static formatCurrency(value: any): string {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;

        if (isNaN(numValue)) {
            return String(value);
        }

        return numValue.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    }

    /**
     * Formata data para DD/MM/YYYY
     */
    static formatDate(value: string): string {
        if (!value) return '-';

        try {
            // Se já está no formato DD/MM/YYYY
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
                return value;
            }

            // Se é ISO date
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('pt-BR');
            }

            return value;
        } catch {
            return value;
        }
    }

    /**
     * Formata tipo de pagamento
     */
    private static formatPaymentType(value: string): string {
        const types: { [key: string]: string } = {
            'pix': 'PIX',
            'cash': 'Dinheiro',
            'credit_card': 'Cartão de Crédito',
            'debit_card': 'Cartão de Débito',
            'transfer': 'Transferência',
            'boleto': 'Boleto',
            'check': 'Cheque'
        };

        return types[value.toLowerCase()] || value;
    }

    /**
     * Formata tipo de entrada
     */
    private static formatEntryType(value: string): string {
        const types: { [key: string]: string } = {
            'tithe': 'Dízimo',
            'offer': 'Oferta',
            'designated': 'Designada',
            'donation': 'Doação',
            'other': 'Outros'
        };

        return types[value.toLowerCase()] || value;
    }

    /**
     * Formata estado civil
     */
    private static formatMaritalStatus(value: string): string {
        const statuses: { [key: string]: string } = {
            'single': 'Solteiro(a)',
            'married': 'Casado(a)',
            'divorced': 'Divorciado(a)',
            'widowed': 'Viúvo(a)',
            'separated': 'Separado(a)'
        };

        return statuses[value.toLowerCase()] || value;
    }

    /**
     * Verifica se o campo é de avatar/foto
     */
    static isAvatarField(key: string): boolean {
        const avatarFields = ['foto', 'avatar', 'photo', 'imagem', 'image', 'picture'];
        return avatarFields.includes(key.toLowerCase());
    }

    /**
     * Verifica se deve ocultar a coluna (campos auxiliares como gênero para cor do avatar)
     */
    static shouldHideColumn(key: string): boolean {
        const hiddenFields = ['genero', 'gender', 'ativo', 'activated', 'active'];
        return hiddenFields.includes(key.toLowerCase());
    }

    /**
     * Filtra headers para exibição (remove colunas ocultas)
     */
    static filterVisibleHeaders(headers: TableHeader[]): TableHeader[] {
        return headers.filter(h => !this.shouldHideColumn(h.key));
    }

    /**
     * Limita os dados para exibição (máximo 10 registros)
     */
    static limitData(resultData: any[] | null, limit: number = 10): any[] {
        if (!resultData) return [];
        return resultData.slice(0, limit);
    }

    /**
     * Verifica se há mais dados além do limite
     */
    static hasMoreData(resultData: any[] | null, limit: number = 10): boolean {
        if (!resultData) return false;
        return resultData.length > limit;
    }

    /**
     * Retorna a quantidade total de registros
     */
    static getTotalCount(resultData: any[] | null): number {
        return resultData?.length || 0;
    }
}
